import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import { CtaWhatsApp } from "../components/Cta";
import { Reveal } from "../components/motion";
import { ARTICLES } from "../data/articles";
import { SITE_URL } from "../config/site";
import NotFound from "./NotFound";

export default function BlogArticle() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);
  if (!article) return <NotFound />;

  const others = ARTICLES.filter((a) => a.slug !== slug);

  const schema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: article.title,
    description: article.metaDesc,
    datePublished: article.date,
    inLanguage: "fr",
    author: { "@type": "Organization", name: "Noveocard", url: SITE_URL },
    publisher: { "@type": "Organization", name: "Noveocard", url: SITE_URL },
    mainEntityOfPage: `${SITE_URL}/conseils/${article.slug}`,
  };

  return (
    <>
      <Seo
        title={article.title}
        description={article.metaDesc}
        path={`/conseils/${article.slug}`}
        jsonld={[schema]}
      />
      <PageHero dark eyebrow={`Conseils — ${article.category}`} titleLines={[article.title]}>
        <p className="flex items-center gap-2 text-sm text-white/55">
          <Clock className="h-4 w-4" /> {article.readTime} de lecture ·{" "}
          {new Date(article.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
        </p>
      </PageHero>

      <article className="bg-white py-16 sm:py-20" data-testid="blog-article">
        <div className="container-nv max-w-3xl">
          <Reveal>
            <p className="text-lg leading-relaxed text-encre sm:text-xl">{article.intro}</p>
          </Reveal>

          {article.sections.map((s) => (
            <Reveal key={s.h2} className="mt-12">
              <h2 className="font-display text-2xl font-bold tracking-tight text-nuit-900 sm:text-3xl">
                {s.h2}
              </h2>
              {s.paras.map((p, i) => (
                <p key={i} className="mt-4 text-base leading-relaxed text-steel">
                  {p}
                </p>
              ))}
              {s.list && (
                <ul className="mt-5 space-y-2.5">
                  {s.list.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-base leading-relaxed text-encre">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-electric" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </Reveal>
          ))}

          {/* CTA */}
          <Reveal className="mt-14">
            <div className="grain relative overflow-hidden rounded-3xl bg-nuit-900 p-8 text-center text-white sm:p-10">
              <p className="font-display text-xl font-bold sm:text-2xl">{article.cta}</p>
              <CtaWhatsApp
                className="mt-6"
                message={`Bonjour Noveocard, j'ai lu votre guide « ${article.title} » et je souhaite en parler.`}
                testid="article-cta-whatsapp"
              />
              {article.relatedSolution && (
                <p className="mt-5 text-sm">
                  <Link
                    to={article.relatedSolution}
                    className="font-display font-semibold text-electric-300 underline-offset-2 hover:underline"
                    data-testid="article-solution-link"
                  >
                    Découvrir la solution Noveocard pour votre métier →
                  </Link>
                </p>
              )}
            </div>
          </Reveal>

          {/* Maillage interne */}
          <nav className="mt-12 border-t border-nuit-900/10 pt-8" aria-label="Autres guides">
            <Link
              to="/conseils"
              className="inline-flex items-center gap-2 font-display text-sm font-semibold text-electric"
              data-testid="article-back-link"
            >
              <ArrowLeft className="h-4 w-4" /> Tous les guides
            </Link>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {others.map((a) => (
                <Link
                  key={a.slug}
                  to={`/conseils/${a.slug}`}
                  className="group flex items-center justify-between gap-3 rounded-2xl border border-nuit-900/10 px-5 py-4 transition-colors hover:border-electric hover:bg-brume"
                >
                  <span className="font-display text-sm font-semibold text-nuit-900">{a.title}</span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-steel transition-transform group-hover:translate-x-1 group-hover:text-electric" />
                </Link>
              ))}
            </div>
          </nav>
        </div>
      </article>
    </>
  );
}
